import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteComment, getAllComments, updateComment } from "../../services/Comment/Comment";
import { getAllPosts } from "../../services/Post/Post";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface AdminComment {
  _id: string;
  idCompany?: { _id: string; name: string };
  comment: string;
  postId?: string;
}

const AdminComments = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [comments, setComments] = useState<AdminComment[]>([]);
  const [postTitles, setPostTitles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [editingComment, setEditingComment] = useState<AdminComment | null>(null);
  const [deletingComment, setDeletingComment] = useState<AdminComment | null>(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([getAllComments(), getAllPosts()])
      .then(([commentsRes, postsRes]: any[]) => {
        setComments(commentsRes.data ?? []);
        const titles: Record<string, string> = {};
        (postsRes.data ?? []).forEach((post: any) => {
          titles[post._id] = post.title;
        });
        setPostTitles(titles);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredComments = useMemo(() => {
    const search = globalFilterValue.trim().toLowerCase();
    if (!search) return comments;
    return comments.filter(
      (c) =>
        c.comment?.toLowerCase().includes(search) ||
        c.idCompany?.name?.toLowerCase().includes(search)
    );
  }, [comments, globalFilterValue]);

  const submitEdit = () => {
    if (!editingComment) return;
    updateComment({
      _id: editingComment._id,
      idCompany: editingComment.idCompany?._id,
      comment: editingComment.comment,
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingComment(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingComment) return;
    deleteComment({ _id: deletingComment._id })
      .then(() => {
        setComments((prev) => prev.filter((c) => c._id !== deletingComment._id));
        setDeletingComment(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:comments")} />

      <div className="admin-page-body">
      <div className="p-input-icon-left mb-3">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => setGlobalFilterValue(e.target.value)}
          placeholder="Keyword Search"
        />
      </div>

      <DataTable
        value={filteredComments}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        emptyMessage={t("common:noResults")}
      >
        <Column
          header={t("common:companies")}
          body={(row: AdminComment) => row.idCompany?.name ?? "-"}
        />
        <Column field="comment" header={t("common:comment")} />
        <Column
          header={t("common:post")}
          body={(row: AdminComment) => (row.postId ? postTitles[row.postId] ?? "-" : "-")}
        />
        <Column
          body={(row: AdminComment) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingComment({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingComment(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={t("common:comment")}
        visible={editingComment != null}
        onHide={() => setEditingComment(null)}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingComment(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingComment && (
          <InputTextarea
            style={{ width: "100%" }}
            rows={4}
            value={editingComment.comment}
            onChange={(e) =>
              setEditingComment({ ...editingComment, comment: e.target.value })
            }
          />
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingComment(null)}
        visible={deletingComment != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingComment(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingComment?.comment}
      </Dialog>
    </div>
  );
};

export default AdminComments;
